import React, { useState, useMemo } from 'react';
import {
  Alert,
  Box,
  Typography,
  Paper,
  IconButton,
  useTheme,
  Tooltip,
  Skeleton,
} from '@mui/material';
import ButtonMAIQ from 'app/components/ReusableUIComponents/LowLevelComponents/buttons/ButtonMAIQ';
import FilterTrayStrip from 'app/components/ReusableUIComponents/HighLevelComponents/FilterTrayStrip';
import { Edit, Add, Delete, Close, Save } from '@mui/icons-material';
import { useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import ModalMAIQ from 'app/components/ModalMAIQ';
import ConfirmationModal from 'app/components/ConfirmationModal';
import DataGridMAIQ from 'app/components/ReusableUIComponents/HighLevelComponents/DataGridMAIQ';
import DataGridTypes from 'types/DataGridTypes';
import Offers from 'app/apiService/offers/offersApiService';
import { selectDealerSelected } from 'app/pages/Header/selectors';
import AddIcon from '@mui/icons-material/Add';
import OfferDetailsModal, { OfferSaveResult } from './OfferDetailsModal';
import { OfferFormData, OfferFormErrors, validateOfferForm, hasValidationErrors } from './components/OfferForm';
import OfferForm from './components/OfferForm';

interface OfferContentTabProps {}

const OfferContentTab: React.FC<OfferContentTabProps> = () => {
  const theme = useTheme();
  const location = useLocation();
  const selectedDealer = useSelector(selectDealerSelected);
  
  // Check if we're in NewAIAvatarLayout context
  const isInAIAvatarLayout = location.pathname.startsWith('/ai-avatar-layout');
  const [modalOpen, setModalOpen] = useState(false);
  const [modalType, setModalType] = useState<
    'lease' | 'leaseFinance' | 'finance'
  >('lease');
  const [modalTitle, setModalTitle] = useState('');
  const [currentOffer, setCurrentOffer] = useState<any>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);
  const [formApiError, setFormApiError] = useState('');

  // Delete confirmation modal state
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [deleteOfferId, setDeleteOfferId] = useState<string>('');
  const [deleteOfferType, setDeleteOfferType] = useState<
    'lease' | 'leaseFinance' | 'finance'
  >('lease');
  const [isDeleting, setIsDeleting] = useState(false);

  // Publish confirmation modal state (grid actions)
  const [publishModalOpen, setPublishModalOpen] = useState(false);
  const [publishOfferId, setPublishOfferId] = useState<string>('');
  const [publishOfferType, setPublishOfferType] = useState<
    'lease' | 'leaseFinance' | 'finance'
  >('lease');
  const [isPublishing, setIsPublishing] = useState(false);

  // Save published offer (create new copy) confirmation in Add/Edit modal
  const [openSavePublishedConfirm, setOpenSavePublishedConfirm] = useState(false);

  // Detail modal state
  const [detailModalOpen, setDetailModalOpen] = useState(false);
  const [selectedOffer, setSelectedOffer] = useState<any>(null);
  const [selectedOfferType, setSelectedOfferType] = useState<
    'lease' | 'leaseFinance' | 'finance'
  >('lease');

  // Offers grid filter: Is active, Expired/Non-expired (at least one), Published/Non-published (at least one)
  const [offersFilter, setOffersFilter] = useState({
    isActive: true,
    showExpired: false,
    showNonExpired: true,
    showPublished: true,
    showNonPublished: true,
  });

  // Form state for offer data
  const [formData, setFormData] = useState<OfferFormData>({
    headline: '',
    description: '',
    content: '',
    leaseLabel: 'Lease For',
    leaseDownLabel: 'Down',
    financeLabel: 'Finance From',
    leaseAmount: '',
    term: '',
    leaseTerm: '',
    leaseDown: '',
    financeRate: '',
    financeTerm: '',
    startDate: '',
    endDate: '',
    year: '',
    make: '',
    model: '',
    trim: '',
    drivetrain: '',
    offerUrl: '',
    offerImageUrl: '',
    disclaimer: '',
  });

  // Validation errors state
  const [formErrors, setFormErrors] = useState<OfferFormErrors>({});

  // Get selected dealer ID
  const getSelectedDealerId = () => {
    if (selectedDealer === 'default') {
      return 'default';
    }
    return selectedDealer?.dealer || 'default';
  };

  // Check if dealer is selected - if not, show message
  if (!getSelectedDealerId() || getSelectedDealerId() === 'default') {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '50vh',
          color: 'text.primary',
          fontSize: '1.5rem',
          fontWeight: 500,
        }}
      >
        <Typography variant="h4" gutterBottom component="div">
          Please select a dealer to view offer content
        </Typography>
      </Box>
    );
  }

  const openModal = (
    type: 'lease' | 'leaseFinance' | 'finance',
    title: string,
    offer?: any,
  ) => {
    setModalType(type);
    setModalTitle(title);
    setCurrentOffer(offer || null);

    // Initialize form data
    if (offer) {
      // Map API response to form fields
      const leaseTerms = offer.lease_terms?.[0] || {};
      const financeTerms = offer.finance_terms || {};

      setFormData({
        headline: offer.headline || '',
        description: offer.description || '',
        content: offer.content || '',
        leaseLabel: 'Lease For', // Default value, not in API
        leaseDownLabel: 'Down', // Default value, not in API
        financeLabel: 'Finance From', // Default value, not in API
        leaseAmount: leaseTerms.monthly_payment?.toString() || '',
        term: leaseTerms.lease_term_months?.toString() || '',
        leaseTerm: leaseTerms.lease_term_months?.toString() || '',
        leaseDown: leaseTerms.due_at_signing?.toString() || '',
        financeRate: financeTerms.apr?.toString() || '',
        financeTerm: financeTerms.term_months?.toString() || '',
        startDate: offer.start_date || '',
        endDate: offer.end_date || '',
        year: offer.vehicle_year?.toString() || '',
        make: offer.vehicle_make || '',
        model: offer.vehicle_model || '',
        trim: offer.trim || '',
        drivetrain: offer.drivetrain || '',
        offerUrl: offer.offer_url || '',
        offerImageUrl: offer.offer_image_url || '',
        disclaimer: offer.disclaimer || '',
      });
    } else {
      // Reset form for new offer
      setFormData({
        headline: '',
        description: '',
        content: '',
        leaseLabel: 'Lease For',
        leaseDownLabel: 'Down',
        financeLabel: 'Finance From',
        leaseAmount: '',
        term: '',
        leaseTerm: '',
        leaseDown: '',
        financeRate: '',
        financeTerm: '',
        startDate: '',
        endDate: '',
        year: '',
        make: '',
        model: '',
        trim: '',
        drivetrain: '',
        offerUrl: '',
        offerImageUrl: '',
        disclaimer: '',
      });
    }

    setModalOpen(true);
    setFormErrors({}); // Clear errors when opening modal
    setFormApiError('');
  };

  const handleFormChange = (field: string, value: string) => {
    setFormData(prev => ({
      ...prev,
      [field]: value,
    }));
    if (formApiError) {
      setFormApiError('');
    }
    
    // Clear field-specific error when user starts typing
    if (formErrors[field as keyof OfferFormErrors]) {
      setFormErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[field as keyof OfferFormErrors];
        return newErrors;
      });
    }
  };

  const getApiErrorMessage = (response: any): string | null => {
    if (!response) {
      return null;
    }

    const errorCode =
      response.errorCode ??
      response.error_code ??
      response.myAutoIQ_error_code ??
      null;
    const statusCode =
      response.statusCode ??
      response.status_code ??
      response.myAutoIQ_status_code ??
      null;
    const statusText =
      typeof response.message === 'string' ? response.message : '';

    const hasErrorCode = typeof errorCode === 'number' && errorCode >= 400;
    const hasErrorStatus =
      typeof statusCode === 'number' && statusCode >= 400;
    const hasFailStatusText = statusText.toLowerCase() === 'fail';

    if (hasErrorCode || hasErrorStatus || hasFailStatusText) {
      return (
        response.message ||
        response.error ||
        statusText ||
        'Failed to save offer. Please review the entered details.'
      );
    }

    return null;
  };

  const performSaveInModal = async () => {
    setIsSubmitting(true);
    try {
      const offerPayload: any = {
        // title: formData.headline,
        headline: formData.headline,
        content: formData.content,
        description: formData.description,
        vehicle_make: formData.make,
        vehicle_model: formData.model,
        vehicle_year: parseInt(formData.year),
        trim: formData.trim,
        drivetrain: formData.drivetrain,
        offer_url: formData.offerUrl,
        offer_image_url: formData.offerImageUrl,
        disclaimer: formData.disclaimer,
        start_date: formData.startDate,
        end_date: formData.endDate,
        is_active: true,
        dealership_id: getSelectedDealerId(),
      };

      if (modalType === 'lease') {
        offerPayload.offer_type = 'LEASE';
        offerPayload.lease_terms = [
          {
            monthly_payment: parseFloat(formData.leaseAmount),
            due_at_signing: parseFloat(formData.leaseDown),
            lease_term_months: parseInt(formData.term),
            annual_mileage_limit: 10000,
            lease_label: formData.leaseLabel || 'Lease For',
            lease_down_label: formData.leaseDownLabel || 'Down',
          },
        ];
      } else if (modalType === 'leaseFinance') {
        offerPayload.offer_type = 'LEASE_FINANCE';
        offerPayload.lease_terms = [
          {
            monthly_payment: parseFloat(formData.leaseAmount),
            due_at_signing: parseFloat(formData.leaseDown),
            lease_term_months: parseInt(formData.leaseTerm),
            annual_mileage_limit: 10000,
            lease_label: formData.leaseLabel || 'Lease For',
            lease_down_label: formData.leaseDownLabel || 'Down',
          },
        ];
        offerPayload.finance_terms = {
          apr: parseFloat(formData.financeRate),
          term_months: parseInt(formData.financeTerm),
          down_payment: parseFloat(formData.leaseDown),
          finance_label: formData.financeLabel || 'Finance From',
        };
      } else if (modalType === 'finance') {
        offerPayload.offer_type = 'FINANCE';
        offerPayload.finance_terms = {
          apr: parseFloat(formData.financeRate),
          term_months: parseInt(formData.financeTerm),
          down_payment: 0,
          finance_label: formData.financeLabel || 'Finance From',
        };
      }

      if (currentOffer?._id) {
        const response = await Offers.editOffer(currentOffer._id, offerPayload);
        const errorMessage = getApiErrorMessage(response);
        if (errorMessage) {
          setFormApiError(errorMessage);
          return;
        }
      } else {
        const response = await Offers.addOffer(offerPayload);
        const errorMessage = getApiErrorMessage(response);
        if (errorMessage) {
          setFormApiError(errorMessage);
          return;
        }
      }

      setModalOpen(false);
      setOpenSavePublishedConfirm(false);
      setFormErrors({});
      setFormApiError('');
      setRefreshKey(prev => prev + 1);
    } catch (error) {
      console.error('Error saving offer:', error);
      const message =
        error instanceof Error
          ? error.message
          : (error as any)?.message || 'Failed to save offer. Please try again.';
      setFormApiError(message);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSave = async () => {
    const errors = validateOfferForm(formData, modalType);
    setFormErrors(errors);
    setFormApiError('');

    if (hasValidationErrors(errors)) {
      console.warn('Validation failed:', errors);
      return;
    }

    if (currentOffer?.status === 'PUBLISHED') {
      setOpenSavePublishedConfirm(true);
      return;
    }

    await performSaveInModal();
  };

  const handleCancel = () => {
    setModalOpen(false);
    setOpenSavePublishedConfirm(false);
    setFormErrors({}); // Clear errors when canceling
    setFormApiError('');
  };

  const handleCellClick = (cell: any, clickedElement?: string) => {
    console.log('Cell clicked:', cell, 'Clicked element:', clickedElement);

    if (cell.field === 'headline') {
      // Determine the offer type based on the current grid
      const offerType = getCurrentOfferType();
      openModal(
        offerType,
        `Edit ${getOfferTypeDisplayName(offerType)} Offer`,
        cell.row,
      );
    } else if (cell.field === 'actions') {
      if (clickedElement === 'edit') {
        const offerType = getCurrentOfferType();
        openModal(
          offerType,
          `Edit ${getOfferTypeDisplayName(offerType)} Offer`,
          cell.row,
        );
      } else if (clickedElement === 'delete') {
        handleDelete(cell.row._id, getCurrentOfferType());
      }
    }
  };

  // Helper function to get current offer type
  const getCurrentOfferType = (): 'lease' | 'leaseFinance' | 'finance' => {
    // This will be determined by which grid is currently active
    // For now, we'll use a default or you can pass it as a parameter
    return 'lease'; // Default fallback
  };

  // Helper function to get display name for offer type
  const getOfferTypeDisplayName = (
    type: 'lease' | 'leaseFinance' | 'finance',
  ): string => {
    switch (type) {
      case 'lease':
        return 'Lease';
      case 'leaseFinance':
        return 'Lease and Finance';
      case 'finance':
        return 'Finance';
      default:
        return 'Offer';
    }
  };

  const handleDelete = async (
    id: string,
    type: 'lease' | 'leaseFinance' | 'finance',
  ) => {
    setDeleteOfferId(id);
    setDeleteOfferType(type);
    setDeleteModalOpen(true);
  };

  const handleDeleteConfirm = async () => {
    setIsDeleting(true);
    try {
      await Offers.deleteOffer(deleteOfferId);
      setRefreshKey(prev => prev + 1);
      setDeleteModalOpen(false);
    } catch (error) {
      console.error('Error deleting offer:', error);
      // You might want to show an error message to the user
    } finally {
      setIsDeleting(false);
    }
  };

  const handleDeleteCancel = () => {
    setDeleteModalOpen(false);
    setDeleteOfferId('');
    setDeleteOfferType('lease');
  };

  const handleOfferUpdate = async (offerId: string, payload: any): Promise<OfferSaveResult> => {
    try {
      const response = await Offers.editOffer(offerId, payload);
      setRefreshKey(prev => prev + 1);
      return {
        success: true,
        message: response?.message || 'Offer updated successfully.',
      };
    } catch (error) {
      console.error('Error updating offer:', error);
      let message = 'Failed to update offer.';
      if (error instanceof Error && error.message) {
        message = error.message;
      } else if ((error as any)?.message) {
        message = (error as any).message;
      }
      return {
        success: false,
        message,
      };
    }
  };

  const handleOfferDelete = async (offerId: string) => {
    try {
      await Offers.deleteOffer(offerId);
      setRefreshKey(prev => prev + 1);
      return true;
    } catch (error) {
      console.error('Error deleting offer:', error);
      return false;
    }
  };

  const handleOfferPublish = async (offerId: string) => {
    await Offers.publishOffer(offerId);
    setRefreshKey(prev => prev + 1);
  };

  const handleIsActiveToggle = async (offerId: string, isActive: boolean) => {
    try {
      await Offers.editOffer(offerId, { is_active: isActive });
      setRefreshKey(prev => prev + 1);
    } catch (error) {
      console.error('Error updating offer active status:', error);
    }
  };

  const handlePublishConfirm = async () => {
    setIsPublishing(true);
    try {
      await handleOfferPublish(publishOfferId);
      setPublishModalOpen(false);
    } catch (error) {
      console.error('Error publishing offer:', error);
    } finally {
      setIsPublishing(false);
    }
  };

  const handlePublishCancel = () => {
    setPublishModalOpen(false);
    setPublishOfferId('');
    setPublishOfferType('lease');
  };

  const renderOfferGrid = (
    type: 'lease' | 'leaseFinance' | 'finance',
    title: string,
  ) => {
    const selectedDealerId = getSelectedDealerId();

    let gridConfig;
    switch (type) {
      case 'lease':
        gridConfig = DataGridTypes.LEASE_OFFERS(selectedDealerId, offersFilter);
        break;
      case 'leaseFinance':
        gridConfig = DataGridTypes.LEASE_FINANCE_OFFERS(selectedDealerId, offersFilter);
        break;
      case 'finance':
        gridConfig = DataGridTypes.FINANCE_OFFERS(selectedDealerId, offersFilter);
        break;
      default:
        gridConfig = DataGridTypes.LEASE_OFFERS(selectedDealerId, offersFilter);
    }

    // Create specific click handler for this grid type
    const handleGridCellClick = (cell: any, clickedElement?: string) => {
      console.log(
        'Grid cell clicked:',
        cell,
        'Clicked element:',
        clickedElement,
        'Grid type:',
        type,
      );

      if (cell.field === 'headline') {
        setSelectedOffer(cell.row);
        setSelectedOfferType(type);
        setDetailModalOpen(true);
      } else if (cell.field === 'is_active' && clickedElement === 'toggle') {
        handleIsActiveToggle(cell.row._id, !cell.row.is_active);
      } else if (cell.field === 'actions') {
        if (clickedElement === 'edit') {
          openModal(
            type,
            `Edit ${getOfferTypeDisplayName(type)} Offer`,
            cell.row,
          );
        } else if (clickedElement === 'publish') {
          setPublishOfferId(cell.row._id);
          setPublishOfferType(type);
          setPublishModalOpen(true);
        } else if (clickedElement === 'delete') {
          handleDelete(cell.row._id, type);
        }
      }
    };

    return (
      <Box sx={{ mb: 4 }}>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            mb: 2,
          }}
        >
          <Typography variant="h6" sx={{ color: 'text.primary', fontWeight: 600 }}>
            {title}
          </Typography>
          <ButtonMAIQ
            variant="contained"
            color="primary"
            startIcon={<AddIcon />}
            onClick={() => openModal(type, `Add New ${title}`)}
          >
            Add New
          </ButtonMAIQ>
        </Box>

        <Paper sx={{ p: 2 }}>
          <DataGridMAIQ
            key={`${type}-${refreshKey}-${selectedDealerId}-${offersFilter.isActive}-${offersFilter.showExpired}-${offersFilter.showNonExpired}-${offersFilter.showPublished}-${offersFilter.showNonPublished}`}
            type={gridConfig}
            onCellClick={handleGridCellClick}
          />
        </Paper>
      </Box>
    );
  };

  return (
    <Box sx={{ p: 3 }}>
      <Paper sx={{ p: 3 }}>
        <Typography
          variant="h6"
          sx={{ color: 'text.primary', mb: 3, fontWeight: 600 }}
        >
          Offer Settings
        </Typography>
        <Typography variant="body2" sx={{ color: 'text.primary', mb: 2 }}>
          Manage your lease and finance offers
        </Typography>

        <FilterTrayStrip
          sticky
          stickyTop={-25}
          items={[
            {
              type: 'switch',
              label: 'Is active:',
              value: offersFilter.isActive,
              onChange: checked =>
                setOffersFilter(prev => ({ ...prev, isActive: checked })),
              switchLabel: 'Active only',
            },
            {
              type: 'checkboxPair',
              groupLabel: 'Expiry:',
              atLeastOne: true,
              options: [
                { id: 'expired', label: 'Expired', checked: offersFilter.showExpired },
                { id: 'nonExpired', label: 'Non-expired', checked: offersFilter.showNonExpired },
              ],
              onChange: (id, checked) => {
                setOffersFilter(prev => {
                  const next =
                    id === 'expired'
                      ? { ...prev, showExpired: checked }
                      : { ...prev, showNonExpired: checked };
                  if (!next.showExpired && !next.showNonExpired)
                    return id === 'expired'
                      ? { ...prev, showNonExpired: true }
                      : { ...prev, showExpired: true };
                  return next;
                });
              },
            },
            {
              type: 'checkboxPair',
              groupLabel: 'Status:',
              atLeastOne: true,
              options: [
                { id: 'published', label: 'Published', checked: offersFilter.showPublished },
                { id: 'nonPublished', label: 'Non-published', checked: offersFilter.showNonPublished },
              ],
              onChange: (id, checked) => {
                setOffersFilter(prev => {
                  const next =
                    id === 'published'
                      ? { ...prev, showPublished: checked }
                      : { ...prev, showNonPublished: checked };
                  if (!next.showPublished && !next.showNonPublished)
                    return id === 'published'
                      ? { ...prev, showNonPublished: true }
                      : { ...prev, showPublished: true };
                  return next;
                });
              },
            },
          ]}
        />

        {renderOfferGrid('lease', 'Lease Offers')}
        {renderOfferGrid('leaseFinance', 'Lease and Finance Offers')}
        {renderOfferGrid('finance', 'Finance Offers')}
      </Paper>

      {/* Offer Modal - User Management Style */}
      <ModalMAIQ
        open={modalOpen}
        onClose={handleCancel}
        hideDefaultCloseButton={true}
      >
        <Paper
          elevation={0}
          sx={{
            width: '800px',
            maxWidth: '90vw',
            maxHeight: '85vh',
            display: 'flex',
            flexDirection: 'column',
            borderRadius: 0,
          }}
        >
          {/* Header - fixed */}
          <Box
            className="modal-header"
            sx={{
              p: 2,
              zIndex: 10,
              flexShrink: 0,
            }}
          >
            <Box
              display="flex"
              justifyContent="space-between"
              alignItems="center"
              width="100%"
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Edit className="modal-header-icon" fontSize="small" />
                <Typography variant="h6" fontWeight="600">
                  {modalTitle}
                </Typography>
              </Box>
              <Box>
                <Tooltip title="Close">
                  <IconButton
                    className="modal-header-button"
                    aria-label="close"
                    onClick={handleCancel}
                    sx={{
                      p: 0.5,
                    }}
                    size="small"
                  >
                    <Close fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Box>
            </Box>
          </Box>

          {/* Content */}
          <Box
            sx={{
              flex: 1,
              display: 'flex',
              flexDirection: 'column',
              overflow: 'hidden',
            }}
          >
            <Box sx={{ flex: 1, overflowY: 'auto', p: 3 }}>
              {formApiError && (
                <Alert severity="error" sx={{ mb: 2 }}>
                  {formApiError}
                </Alert>
              )}
              <OfferForm
                formData={formData}
                modalType={modalType}
                onFormChange={handleFormChange}
                errors={formErrors}
              />

            </Box>

            {/* Action Buttons - fixed at bottom */}
            <Box
              sx={{
                p: 2,
                display: 'flex',
                justifyContent: 'flex-end',
                backgroundColor: theme.palette.background.paper,
                borderTop: `1px solid ${theme.palette.divider}`,
                zIndex: 9,
                gap: 2,
                flexShrink: 0,
              }}
            >
              <ButtonMAIQ
                variant="outlined"
                onClick={handleCancel}
                disabled={isSubmitting}
              >
                Cancel
              </ButtonMAIQ>
              {isSubmitting ? (
                <Skeleton
                  variant="rectangular"
                  width={120}
                  height={36}
                  sx={{
                    borderRadius: 1,
                    bgcolor: theme.palette.action.hover,
                  }}
                />
              ) : (
                <ButtonMAIQ
                  variant="contained"
                  color="primary"
                  onClick={handleSave}
                  startIcon={<Save />}
                >
                  {currentOffer ? 'Save Changes' : 'Create Offer'}
                </ButtonMAIQ>
              )}
            </Box>
          </Box>
        </Paper>
      </ModalMAIQ>

      {/* Delete Confirmation Modal */}
      <ConfirmationModal
        open={deleteModalOpen}
        onClose={handleDeleteCancel}
        onConfirm={handleDeleteConfirm}
        title="Confirm Deletion"
        message={`Are you sure you want to delete this ${getOfferTypeDisplayName(
          deleteOfferType,
        )} offer? This action cannot be undone.`}
        loading={isDeleting}
      />

      {/* Publish Confirmation Modal (grid actions) */}
      <ConfirmationModal
        open={publishModalOpen}
        onClose={handlePublishCancel}
        onConfirm={handlePublishConfirm}
        title="Publish Offer"
        message={`Are you sure you want to publish this ${getOfferTypeDisplayName(
          publishOfferType,
        )} offer? Once published, it cannot be edited directly.`}
        loading={isPublishing}
        confirmLabel="Publish"
        loadingLabel="Publishing..."
      />

      {/* Save published offer as new copy (Add/Edit modal) */}
      <ConfirmationModal
        open={openSavePublishedConfirm}
        onClose={() => !isSubmitting && setOpenSavePublishedConfirm(false)}
        onConfirm={() => performSaveInModal()}
        title="Save as new copy"
        message="This offer is already published. Saving will create a new copy of the offer. Do you want to save and create a new copy of this offer?"
        loading={isSubmitting}
        confirmLabel="Save"
        loadingLabel="Saving..."
      />

      {/* Offer Details Modal */}
      {selectedOffer && (
        <OfferDetailsModal
          open={detailModalOpen}
          onClose={() => {
            setDetailModalOpen(false);
            setSelectedOffer(null);
          }}
          data={selectedOffer}
          offerType={selectedOfferType}
          onSave={async (payload) => {
            const result = await handleOfferUpdate(selectedOffer._id, payload);
            if (result.success) {
              setDetailModalOpen(false);
              setSelectedOffer(null);
            }
            return result;
          }}
          onDelete={async () => {
            const success = await handleOfferDelete(selectedOffer._id);
            if (success) {
              setDetailModalOpen(false);
              setSelectedOffer(null);
            }
          }}
          onPublish={async () => {
            await handleOfferPublish(selectedOffer._id);
            setDetailModalOpen(false);
            setSelectedOffer(null);
          }}
        />
      )}
    </Box>
  );
};

export default OfferContentTab;
